import React from 'react';
import { cn } from '../utils';
import { Button, type ButtonProps } from '../components/Button';
import { Icon } from '../components/icons/Icon';
import { IconName } from '../icons/names';
import { useTranslation } from '../providers/UIProvider';

export interface PageProps {
  className?: string;
  children?: React.ReactNode;
  testId?: string;
}

export interface PageHeaderProps {
  className?: string;
  children?: React.ReactNode;
  testId?: string;
}

export interface PageTitleProps {
  className?: string;
  children?: React.ReactNode;
  testId?: string;
}

export interface PageActionProps {
  className?: string;
  children?: React.ReactNode;
  testId?: string;
  // Button props - if provided, Action will render as Button
  icon?: IconName | React.ReactNode;
  variant?: ButtonProps['variant'];
  size?: ButtonProps['size'];
  onClick?: ButtonProps['onClick'];
  disabled?: ButtonProps['disabled'];
  iconPosition?: ButtonProps['iconPosition'];
  outline?: ButtonProps['outline'];
  ariaLabel?: string; // For accessibility when text is hidden on mobile
}

export interface PageContentProps {
  className?: string;
  children?: React.ReactNode;
  testId?: string;
  /** `column` makes the content a viewport-bounded flex column so headers stay fixed and bodies scroll. */
  layout?: 'default' | 'column';
}

export interface PageSidebarProps {
  className?: string;
  children?: React.ReactNode;
  testId?: string;
  /** Collapses the sidebar to a thin rail; children stay mounted but hidden. */
  collapsed?: boolean;
  /** Renders the edge toggle button; without it the sidebar is not collapsible. */
  onCollapsedChange?: (collapsed: boolean) => void;
}

export interface PageContentHeaderProps {
  className?: string;
  children?: React.ReactNode;
  testId?: string;
}

export interface PageContentBodyProps {
  className?: string;
  children?: React.ReactNode;
  testId?: string;
}

export interface PageContentMainProps {
  className?: string;
  children?: React.ReactNode;
  testId?: string;
}

export type PageContentSidebarVariant = 'mobile' | 'desktop';

export interface PageContentSidebarProps {
  className?: string;
  /** Render node, or a render-prop receiving the active `variant` to vary content per viewport. */
  children?: React.ReactNode | ((variant: PageContentSidebarVariant) => React.ReactNode);
  testId?: string;
}

const PageRoot: React.FC<PageProps> = ({ children, className, testId }) => {
  return (
    <div
      data-testid={testId}
      className={cn(
        'grid gap-0 grid-cols-[1fr_auto] h-full',
        'grid-rows-[auto_auto_1fr]',
        "[grid-template-areas:'header_header'_'title_action'_'page_page']",
        "xl:![grid-template-areas:'sidebar_header_header'_'sidebar_title_action'_'sidebar_page_page'] xl:grid-cols-[auto_1fr_auto]",
        'bg-background-base animate-fade-in',
        className
      )}
    >
      {children}
    </div>
  );
};

PageRoot.displayName = 'Page';

const Header: React.FC<PageHeaderProps> = ({ children, className, testId }) => (
  <header
    data-testid={testId}
    className={cn('px-lg pt-lg -mb-md text-xs [grid-area:header]', className)}
  >
    {children}
  </header>
);
Header.displayName = 'Page.Header';

const Title: React.FC<PageTitleProps> = ({ children, className, testId }) => (
  <div
    data-testid={testId}
    className={cn(
      'px-lg pt-lg pb-md text-2xl [grid-area:title] flex items-center gap-sm',
      className
    )}
  >
    {children}
  </div>
);
Title.displayName = 'Page.Title';

const Action: React.FC<PageActionProps> = ({
  children,
  className,
  testId,
  icon,
  variant,
  size,
  onClick,
  disabled,
  iconPosition,
  outline,
  ariaLabel
}) => {
  // If icon or variant is provided, render as Button
  const shouldRenderAsButton = icon !== undefined || variant !== undefined;

  if (shouldRenderAsButton) {
    // Determine which span contains the text based on icon position
    // If icon is on left (default), text is in 2nd span. If icon is on right, text is in 1st span.
    const textSpanSelector =
      iconPosition === 'right'
        ? '[&_button>span:first-child]:hidden sm:[&_button>span:first-child]:inline'
        : '[&_button>span:nth-child(2)]:hidden sm:[&_button>span:nth-child(2)]:inline';

    return (
      <div
        role='toolbar'
        data-testid={testId}
        className={cn(
          'px-lg pt-lg [grid-area:action] flex items-center justify-end',
          textSpanSelector,
          className
        )}
      >
        <Button
          icon={icon}
          variant={variant}
          size={size}
          onClick={onClick}
          disabled={disabled}
          iconPosition={iconPosition}
          outline={outline}
          aria-label={ariaLabel || (typeof children === 'string' ? children : undefined)}
        >
          {children}
        </Button>
      </div>
    );
  }

  // Otherwise, render as div (backward compatibility)
  return (
    <div
      role='toolbar'
      data-testid={testId}
      className={cn('px-lg pt-lg [grid-area:action]', className)}
    >
      {children}
    </div>
  );
};
Action.displayName = 'Page.Action';

const Content: React.FC<PageContentProps> = ({
  children,
  className,
  testId,
  layout = 'default'
}) => (
  <div
    data-testid={testId}
    className={cn(
      'px-lg [grid-area:page] overflow-auto',
      layout === 'column' && 'px-lg md:flex md:flex-col md:overflow-hidden',
      className
    )}
  >
    {children}
  </div>
);
Content.displayName = 'Page.Content';

const Sidebar: React.FC<PageSidebarProps> = ({
  children,
  className,
  testId,
  collapsed = false,
  onCollapsedChange
}) => {
  const t = useTranslation('components.pageSidebar');
  const contentId = React.useId();

  if (!onCollapsedChange) {
    return (
      <aside
        data-testid={testId}
        className={cn(
          'text-xs hidden xl:!block xl:[grid-area:sidebar] w-80 p-lg border-r border-border-base',
          className
        )}
      >
        {children}
      </aside>
    );
  }

  const label = collapsed ? t('expand') : t('collapse');

  return (
    <aside
      data-testid={testId}
      className={cn(
        'relative text-xs hidden xl:!block xl:[grid-area:sidebar] border-r border-border-base transition-[width]',
        collapsed ? 'w-0 p-0' : 'w-80 p-lg',
        className
      )}
    >
      <div id={contentId} className={cn('h-full', collapsed && 'hidden')}>
        {children}
      </div>
      <button
        type='button'
        aria-expanded={!collapsed}
        aria-controls={contentId}
        aria-label={label}
        title={label}
        data-testid={testId ? `${testId}-toggle` : undefined}
        onClick={() => onCollapsedChange(!collapsed)}
        className={cn(
          'absolute top-1/2 z-10 flex h-10 w-5 -translate-y-1/2 items-center justify-center border border-border-base bg-background-input text-foreground-muted transition-colors hover:text-foreground-base',
          // Collapsed, it hangs off the page edge as a tab; open, it straddles the border.
          collapsed ? 'left-0 rounded-r-md border-l-0' : '-right-2.5 rounded-md'
        )}
      >
        <Icon name='chevron-down' rotate={collapsed ? 270 : 90} className='h-4 w-4' />
      </button>
    </aside>
  );
};
Sidebar.displayName = 'Page.Sidebar';

/** Fixed Non-shrinking header row inside a flex-column `Page.Content`. */
const ContentHeader: React.FC<PageContentHeaderProps> = ({ children, className, testId }) => (
  <div data-testid={testId} className={cn('md:shrink-0 py-xs lg:py-md', className)}>
    {children}
  </div>
);
ContentHeader.displayName = 'Page.ContentHeader';

/** Two-column body (main + sidebar) that fills the remaining height of a `layout='column'` content. */
const ContentBody: React.FC<PageContentBodyProps> = ({ children, className, testId }) => (
  <div
    data-testid={testId}
    className={cn(
      'grid grid-cols-1 gap-lg md:grid-cols-[7fr_3fr] md:min-h-0 md:flex-1 md:overflow-hidden',
      className
    )}
  >
    {children}
  </div>
);
ContentBody.displayName = 'Page.ContentBody';

/** Main column inside `Page.ContentBody` with its own scroll area on desktop. */
const ContentMain: React.FC<PageContentMainProps> = ({ children, className, testId }) => (
  <div
    data-testid={testId}
    className={cn('md:min-h-0 md:overflow-y-auto md:pr-sm pb-0 md:pb-lg', className)}
  >
    {children}
  </div>
);
ContentMain.displayName = 'Page.ContentMain';

/** Right sidebar with its own scroll area (hidden scrollbar)
 * on desktop and top rendering on mobile.
 **/
const ContentSidebar: React.FC<PageContentSidebarProps> = ({ children, className, testId }) => {
  const render = (variant: PageContentSidebarVariant) =>
    typeof children === 'function' ? children(variant) : children;

  return (
    <>
      {/* Mobile: rendered below the main content */}
      <div className='md:hidden order-last space-y-md'>{render('mobile')}</div>

      {/* Desktop: own scroll area with hidden scrollbar */}
      <aside
        data-testid={testId}
        className={cn(
          'hidden md:block space-y-md md:min-h-0 md:overflow-y-auto scrollbar-none',
          className
        )}
      >
        {render('desktop')}
      </aside>
    </>
  );
};
ContentSidebar.displayName = 'Page.ContentSidebar';

// --------------------
// Compose
// --------------------
export const Page = Object.assign(PageRoot, {
  Header,
  Title,
  Action,
  Content,
  Sidebar,
  ContentHeader,
  ContentBody,
  ContentMain,
  ContentSidebar
});
